import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { colors } from '../theme';
import UidHeader from './UidHeader';
import UidMap from './UidMap';
import useFarmContext from './useFarmContext';
import { formatLocal, formatCoordinate, isValidCoordinate } from './utils';

const API_BASE = process.env.REACT_APP_API_URL || '';
const SCANS_PAGE_SIZE = 25;

export default function FarmDevice() {
  const { tenant, requestRefresh } = useFarmContext();
  const { uid } = useParams();
  const { getToken } = useAuth();

  const [tab, setTab] = useState('overview');
  const [scansPage, setScansPage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [trail, setTrail] = useState({ loading: false, error: '', data: [] });

  useEffect(() => {
    setScansPage(1);
  }, [uid]);

  useEffect(() => {
    let cancelled = false;

    async function loadDetail() {
      setLoading(true);
      setError('');
      try {
        const token = await getToken();
        if (!token) throw new Error('Missing auth token');
        const params = new URLSearchParams({
          page: String(scansPage),
          pageSize: String(SCANS_PAGE_SIZE),
        });
        const response = await fetch(`${API_BASE}/farm/${tenant}/uids/${encodeURIComponent(uid)}?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body?.error || `Request failed (${response.status})`);
        }
        const body = await response.json();
        if (!cancelled) {
          setDetail(body);
        }
      } catch (err) {
        console.error('Failed to load device detail', err);
        if (!cancelled) {
          setError('Unable to load device data. Please try again.');
          setDetail(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    if (tenant && uid) {
      loadDetail();
    }

    return () => {
      cancelled = true;
    };
  }, [tenant, uid, scansPage, getToken]);

  useEffect(() => {
    if (tab !== 'map' || !tenant || !uid || trail.data.length) {
      return;
    }

    let cancelled = false;
    setTrail(prev => ({ ...prev, loading: true, error: '' }));

    async function loadTrail() {
      try {
        const token = await getToken();
        if (!token) throw new Error('Missing auth token');
        const response = await fetch(`${API_BASE}/farm/${tenant}/uids/${encodeURIComponent(uid)}/trail`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body?.error || `Trail request failed (${response.status})`);
        }
        const body = await response.json();
        if (!cancelled) {
          setTrail({ loading: false, error: '', data: Array.isArray(body.data) ? body.data : [] });
        }
      } catch (err) {
        console.error('Failed to load trail', err);
        if (!cancelled) {
          setTrail({ loading: false, error: 'Unable to load device trail.', data: [] });
        }
      }
    }

    loadTrail();
    return () => {
      cancelled = true;
    };
  }, [tab, tenant, uid, trail.data.length, getToken]);

  const recentScans = useMemo(() => {
    if (!detail?.scans?.data) return [];
    return detail.scans.data.slice(0, 5);
  }, [detail]);

  const scansTotalPages = detail?.scans?.total
    ? Math.max(1, Math.ceil(detail.scans.total / SCANS_PAGE_SIZE))
    : 1;

  function handleNameSaved(name) {
    setDetail(prev => (prev ? { ...prev, customName: name || null } : prev));
    requestRefresh();
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
      <Link to={`/farm/${tenant}`} style={backLinkStyle}>
        ← Back to herd
      </Link>

      {error && <p style={{ color: colors.danger }}>{error}</p>}
      {loading && <p>Loading device…</p>}

      {detail && (
        <>
          <UidHeader
            tenant={tenant}
            uid={detail.uid}
            customName={detail.customName}
            firstSeen={detail.firstSeen}
            lastSeen={detail.lastSeen}
            scansCount={detail.scansCount}
            onNameSaved={handleNameSaved}
          />

          <div style={tabsRowStyle}>
            <button
              type="button"
              onClick={() => setTab('overview')}
              style={tabButtonStyle(tab === 'overview')}
            >
              Overview
            </button>
            <button
              type="button"
              onClick={() => setTab('map')}
              style={tabButtonStyle(tab === 'map')}
            >
              Map
            </button>
            <button
              type="button"
              onClick={() => setTab('scans')}
              style={tabButtonStyle(tab === 'scans')}
            >
              Scans
            </button>
          </div>

          {tab === 'overview' && (
            <div style={overviewGridStyle}>
              <div style={overviewCardStyle}>
                <h2 style={cardTitleStyle}>Latest location</h2>
                {isValidCoordinate(detail?.lastLocation?.lat) && isValidCoordinate(detail?.lastLocation?.lon) ? (
                  <>
                    <p style={cardBodyStyle}>
                      <strong>Latitude:</strong> {formatCoordinate(detail.lastLocation.lat)} <br />
                      <strong>Longitude:</strong> {formatCoordinate(detail.lastLocation.lon)}
                    </p>
                    <p style={cardBodyStyle}>
                      <strong>Last seen:</strong>{' '}
                      <span title={detail.lastSeen || ''}>{formatLocal(detail.lastSeen)}</span>
                    </p>
                    <UidMap
                      points={[
                        { lat: detail.lastLocation.lat, lon: detail.lastLocation.lon },
                      ]}
                      height={260}
                      showTrail={false}
                      autoFit={false}
                    />
                  </>
                ) : (
                  <p style={cardBodyStyle}>No location recorded yet.</p>
                )}
              </div>

              <div style={overviewCardStyle}>
                <h2 style={cardTitleStyle}>Recent scans</h2>
                {recentScans.length === 0 ? (
                  <p style={cardBodyStyle}>No scans recorded.</p>
                ) : (
                  <ul style={scanListStyle}>
                    {recentScans.map(scan => (
                      <li key={`${scan.timestamp}-${scan.lat}-${scan.lon}`} style={scanItemStyle}>
                        <span style={scanTimestampStyle} title={scan.timestamp}>
                          {formatLocal(scan.timestamp)}
                        </span>
                        <span>
                          {formatCoordinate(scan.lat)}, {formatCoordinate(scan.lon)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {tab === 'map' && (
            <div style={mapContainerStyle}>
              {trail.loading && <p>Loading trail…</p>}
              {trail.error && <p style={{ color: colors.danger }}>{trail.error}</p>}
              {!trail.loading && !trail.error && trail.data.length === 0 && (
                <p>No trail points recorded yet.</p>
              )}
              {!trail.loading && !trail.error && trail.data.length > 0 && (
                <UidMap
                  points={trail.data}
                  height={540}
                  showTrail
                  autoFit
                  maxZoom={14}
                />
              )}
            </div>
          )}

          {tab === 'scans' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Timestamp</th>
                      <th style={thStyle}>Latitude</th>
                      <th style={thStyle}>Longitude</th>
                      <th style={thStyle}>Ingested</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.scans.data.map(scan => (
                      <tr key={`${scan.timestamp}-${scan.lat}-${scan.lon}`}>
                        <td style={tdStyle} title={scan.timestamp}>
                          {formatLocal(scan.timestamp)}
                        </td>
                        <td style={tdStyle}>{formatCoordinate(scan.lat)}</td>
                        <td style={tdStyle}>{formatCoordinate(scan.lon)}</td>
                        <td style={tdStyle}>{scan._ingestedAtUtc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={paginationStyle}>
                <button
                  type="button"
                  onClick={() => setScansPage(prev => Math.max(1, prev - 1))}
                  disabled={scansPage === 1}
                  style={{ ...paginationButtonStyle, opacity: scansPage === 1 ? 0.4 : 1 }}
                >
                  Previous
                </button>
                <span>
                  Page {scansPage} of {scansTotalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setScansPage(prev => Math.min(scansTotalPages, prev + 1))}
                  disabled={scansPage >= scansTotalPages}
                  style={{ ...paginationButtonStyle, opacity: scansPage >= scansTotalPages ? 0.4 : 1 }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

const backLinkStyle = {
  color: colors.primary,
  textDecoration: 'none',
  fontWeight: 600,
};

const tabsRowStyle = {
  display: 'flex',
  gap: '0.75rem',
  flexWrap: 'wrap',
};

const tabButtonStyle = active => ({
  padding: '0.55rem 1.2rem',
  borderRadius: '999px',
  border: active ? 'none' : `1px solid ${colors.border}`,
  backgroundColor: active ? colors.primary : 'transparent',
  color: active ? colors.background : colors.text,
  fontWeight: 600,
  cursor: 'pointer',
});

const overviewGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '1.5rem',
};

const overviewCardStyle = {
  padding: '1.25rem',
  borderRadius: '16px',
  border: '1px solid rgba(70,187,137,0.18)',
  backgroundColor: 'rgba(70,187,137,0.06)',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
};

const cardTitleStyle = {
  margin: 0,
  fontSize: '1.25rem',
};

const cardBodyStyle = {
  margin: 0,
  fontSize: '0.95rem',
  lineHeight: 1.4,
};

const scanListStyle = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
};

const scanItemStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
};

const scanTimestampStyle = {
  fontWeight: 600,
  fontSize: '0.95rem',
};

const mapContainerStyle = {
  borderRadius: '18px',
  border: '1px solid rgba(70,187,137,0.18)',
  padding: '1rem',
  backgroundColor: 'rgba(70,187,137,0.05)',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
};

const thStyle = {
  textAlign: 'left',
  padding: '0.75rem',
  borderBottom: `2px solid ${colors.border}`,
  fontSize: '0.85rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const tdStyle = {
  padding: '0.75rem',
  borderBottom: `1px solid ${colors.border}`,
  fontSize: '0.95rem',
};

const paginationStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
};

const paginationButtonStyle = {
  backgroundColor: colors.primary,
  color: colors.background,
  border: 'none',
  borderRadius: '999px',
  padding: '0.45rem 1.2rem',
  fontWeight: 600,
  cursor: 'pointer',
};
